import { useEffect, useLayoutEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence, useReducedMotion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUp, CheckCheck, Plus } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import AriOrb from "@/components/ari/AriOrb";
import { NetworkField } from "@/components/marketing/NetworkField";

const GOLD = "#D4AF37";
const EASE = [0.22, 1, 0.36, 1];
const SLOW = [0.16, 1, 0.3, 1];

// ---------------------------------------------------------------------------------------------
// HOMEPAGE HERO — an ultra-realistic ARI conversation held inside a premium glass panel.
// Emotional arc: PRESENCE → a rich MULTI-TURN chat (manager asks → ARI thinks → answers → shows a
// data card → manager follows up → ARI thinks → answers) → BRAND. ARI is champagne GOLD, the human
// manager is VIOLET — the platform's own fixed scheme (lib/accents.js). Every surface is photoreal
// glass so it reads as a genuine, high-end product recording.
// ---------------------------------------------------------------------------------------------
const ARI = { solid: "#D4AF37", glow: "rgba(212,175,55,0.5)", soft: "rgba(212,175,55,0.14)", border: "rgba(212,175,55,0.30)", faint: "rgba(212,175,55,0.14)", ring: "rgba(212,175,55,0.34)", grad: "linear-gradient(140deg,#F6E3A6,#D4AF37)" };
const MGR = { solid: "#B79CF2", glow: "rgba(183,156,242,0.5)", soft: "rgba(183,156,242,0.16)", border: "rgba(183,156,242,0.32)" };

// Three authentic exchanges — each a full two-turn conversation from a different restaurant.
const CONVOS = [
  {
    restaurant: "The Copper Table",
    owner: "James Whitmore", role: "Owner",
    q: "Are we overspending on labour this week?",
    a: "Yes — labour's at 31% of sales, three points over your 28% target. Trimming Saturday's over-cover brings it back in line.",
    vis: { title: "Labour · This week", unit: "%", value: 31, delta: "+3 pts over target", spark: [46, 52, 49, 58, 63, 71, 78],
      steps: ["Reading this week's rota", "Pulling POS sales", "Comparing to 28% target"] },
    q2: "How much would trimming Saturday save us?",
    a2: "About £430 this week. I've drafted the revised Saturday rota — approve it and I'll notify the team.",
  },
  {
    restaurant: "Saffron & Sea",
    owner: "Amara Okafor", role: "General Manager",
    q: "How busy are we this Friday night?",
    a: "Fully committed — 142 covers booked, 96% of capacity. Two large parties at 8pm; stagger the second seating to protect service.",
    vis: { title: "Covers · Friday", unit: "", value: 142, delta: "96% of capacity", spark: [38, 44, 52, 60, 72, 84, 95],
      steps: ["Checking Friday bookings", "Counting covers vs capacity", "Scanning large parties"] },
    q2: "Do we have enough front-of-house on?",
    a2: "You're one short for that volume. I'd add a server 6–11pm — Amara's available and under her weekly hours.",
  },
  {
    restaurant: "Nonna's Kitchen",
    owner: "Sofia Ricci", role: "Owner",
    q: "Is our food cost creeping up?",
    a: "A little — food cost is 32% this month, up from 29%. Beef and olive oil are the drivers; locking in this week's price holds it.",
    vis: { title: "Food cost · This month", unit: "%", value: 32, delta: "+3 pts vs last month", spark: [40, 43, 47, 45, 52, 58, 64],
      steps: ["Reviewing supplier invoices", "Tracking beef & oil prices", "Comparing to last month"] },
    q2: "Which supplier should I lock in?",
    a2: "Bidfood — their beef price is 8% below Brakes this week. I can prepare the order for your sign-off.",
  },
];

// The surfaces ARI reads across — shown as elegant animated steps in the introduction.
const READS = [
  { k: "Reservations", d: "every booking and table" },
  { k: "Service", d: "every request on the floor" },
  { k: "Costs", d: "every invoice and margin" },
  { k: "Guests", d: "every regular and their taste" },
];

// Cinematic story beats for the ARI introduction — shown one dramatic line at a time, like a
// film title sequence (not a feature list). Resolves to the ARI brand frame.
const STORY = [
  "Every service, a thousand quiet decisions.",
  "Reservations, service, costs, guests —",
  "ARI reads them all, as they happen.",
];

const CountUp = ({ to, active, dur = 1.1 }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) { setN(0); return; }
    let raf; const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / (dur * 1000));
      setN(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, active, dur]);
  return <>{n}</>;
};

// A single ARI turn — the gold orb (in its thinking state) + label, then a typing indicator that
// resolves into a champagne-gold answer bubble. Reused for both ARI replies.
const AriTurn = ({ thinking, answerShown, text, time }) => (
  <motion.div layout className="self-start shrink-0 max-w-[88%] flex flex-col" style={{ transformPerspective: 720 }}
    initial={{ opacity: 0, y: 12, rotateX: 14 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} transition={{ type: "spring", stiffness: 130, damping: 18, mass: 0.8 }}>
    <div className="flex items-center gap-2 mb-1 pl-0.5">
      <AriOrb size={20} state={thinking ? "thinking" : "insight"} className="shrink-0" />
      <span data-no-translate className="hc-crisp font-mono-lux text-[8.5px] tracking-[0.22em] uppercase" style={{ color: ARI.solid }}>ARI</span>
    </div>
    <div className="min-w-0 pl-0.5">
      {thinking ? (
        <motion.div data-testid="hero-ari-typing" className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-2xl rounded-tl-[7px]"
          initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, ease: EASE }}
          style={{ background: "linear-gradient(180deg,rgba(212,175,55,0.16),rgba(212,175,55,0.05))", border: `1px solid ${ARI.border}`, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.16)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}>
          {[0, 1, 2].map((d) => (
            <span key={d} className="hc-tdot w-1.5 h-1.5 rounded-full" style={{ background: ARI.solid, animationDelay: `${d * 0.18}s` }} />
          ))}
        </motion.div>
      ) : (
        <motion.div data-testid="hero-ari-bubble" style={{ transformPerspective: 720 }}
          initial={{ opacity: 0, y: 10, rotateX: 18, scale: 0.96 }} animate={{ opacity: answerShown ? 1 : 0, y: answerShown ? 0 : 10, rotateX: answerShown ? 0 : 18, scale: answerShown ? 1 : 0.96 }} transition={{ type: "spring", stiffness: 130, damping: 17, mass: 0.8 }}>
          <div className="hc-crisp relative overflow-hidden text-[13px] font-semibold leading-snug px-3.5 py-2 rounded-2xl rounded-tl-[7px] tracking-[-0.01em]"
            style={{ background: "linear-gradient(180deg,rgba(212,175,55,0.17),rgba(212,175,55,0.055))", border: `1px solid ${ARI.border}`, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18),0 12px 28px -14px rgba(0,0,0,0.75)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}>
            <span data-no-translate className="hc-ari-text">{text}</span>
            <span aria-hidden className="hc-sheen" />
          </div>
          <div className="flex items-center gap-1 mt-1 pl-1">
            <span className="hc-crisp text-[9px] text-white/30 font-mono-lux tracking-wide">{time}</span>
          </div>
        </motion.div>
      )}
    </div>
  </motion.div>
);

// A manager (violet) question bubble on the right, with timestamp + read ticks.
const ManagerBubble = ({ text, time, testId }) => (
  <motion.div layout data-testid={testId} className="self-end shrink-0 max-w-[84%] flex flex-col items-end" style={{ transformPerspective: 720 }}
    initial={{ opacity: 0, y: 14, rotateX: -16, scale: 0.96 }} animate={{ opacity: 1, y: 0, rotateX: 0, scale: 1 }} transition={{ type: "spring", stiffness: 130, damping: 17, mass: 0.8 }}>
    <div className="hc-crisp relative overflow-hidden text-[13px] leading-snug text-white/95 px-3.5 py-2 rounded-2xl rounded-tr-[7px]"
      style={{ background: "linear-gradient(180deg,rgba(183,156,242,0.28),rgba(126,104,190,0.16))", border: `1px solid ${MGR.border}`, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18),0 10px 24px -12px rgba(0,0,0,0.6)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)" }}>
      <span data-no-translate>{text}</span>
      <span aria-hidden className="hc-sheen" />
    </div>
    <div className="flex items-center gap-1 mt-1 pr-1">
      <span className="hc-crisp text-[9px] text-white/35 font-mono-lux tracking-wide">{time}</span>
      <CheckCheck size={12} style={{ color: MGR.solid }} strokeWidth={2.4} />
    </div>
  </motion.div>
);

// The hero's living-intelligence environment, brought INSIDE the panel and clipped to its rounded
// corners — soft champagne light fields for depth + a pristine connected NETWORK of glowing nodes
// and fine strings that link and pulse. Sits behind all chat content; never interferes with it.
const PANEL_ORBS = [
  { left: "-12%", top: "-10%", size: "78%", anim: "paA", d: "26s", delay: "0s", blur: 26, bg: "radial-gradient(closest-side, rgba(150,120,46,0.13), rgba(150,120,46,0.04) 46%, transparent 72%)" },
  { left: "42%", top: "26%", size: "82%", anim: "paB", d: "34s", delay: "3s", blur: 30, bg: "radial-gradient(closest-side, rgba(166,134,62,0.09), transparent 70%)" },
  { left: "8%", top: "52%", size: "70%", anim: "paC", d: "40s", delay: "1.5s", blur: 32, bg: "radial-gradient(closest-side, rgba(150,128,196,0.08), transparent 72%)" },
  { left: "50%", top: "60%", size: "64%", anim: "paD", d: "30s", delay: "5s", blur: 28, bg: "radial-gradient(closest-side, rgba(176,144,74,0.06), transparent 70%)" },
];

export const PanelAmbience = ({ testId = "hero-panel-ambience", network = 14 }) => (
  <div className="absolute inset-0 z-[1] overflow-hidden pointer-events-none" aria-hidden="true"
    data-testid={testId}
    style={{ clipPath: "inset(0)", WebkitClipPath: "inset(0)" }}>
    <style>{`
      @keyframes paA{0%,100%{transform:translate3d(0,0,0) scale(1)}50%{transform:translate3d(16%,-12%,0) scale(1.12)}}
      @keyframes paB{0%,100%{transform:translate3d(0,0,0) scale(1.08)}50%{transform:translate3d(-15%,13%,0) scale(1)}}
      @keyframes paC{0%,100%{transform:translate3d(0,0,0) scale(1)}50%{transform:translate3d(13%,15%,0) scale(1.16)}}
      @keyframes paD{0%,100%{transform:translate3d(0,0,0) scale(1.04)}50%{transform:translate3d(-13%,-11%,0) scale(1)}}
      .pa-orb{will-change:transform;animation-timing-function:cubic-bezier(0.45,0,0.25,1);animation-iteration-count:infinite}
      @media (prefers-reduced-motion: reduce){.pa-orb{animation:none!important}}
    `}</style>

    {/* 1. solid charcoal-gold base — matches the charcoal-gold used across the landing hero
          (#15110A / #1C1710 / #17120A) so the card reads as part of the same surface. OPAQUE,
          crisp, zero dark gaps. */}
    <div className="absolute inset-0" style={{ background: "linear-gradient(152deg, #15110A 0%, #1C1710 46%, #191308 70%, #17120A 100%)" }} />
    {/* 2. deep charcoal-gold glow pools for pristine depth — muted so they stay in the charcoal-gold
          family and blend seamlessly (no bright olive patch), strong-but-subtle at the base */}
    <div className="absolute inset-0" style={{ background: "radial-gradient(135% 100% at 50% 122%, rgba(138,110,46,0.13), rgba(120,96,38,0.04) 46%, transparent 80%)" }} />
    <div className="absolute inset-x-0 top-0 h-2/3" style={{ background: "radial-gradient(120% 82% at 50% -8%, rgba(150,120,44,0.07), transparent 66%)" }} />

    {/* roaming luminous fields — large, deeply feathered champagne light for glassy depth */}
    {PANEL_ORBS.map((o, i) => (
      <div key={i} className="pa-orb absolute rounded-full"
        style={{ left: o.left, top: o.top, width: o.size, height: o.size,
                 filter: `blur(${o.blur}px)`, background: o.bg,
                 animationName: o.anim, animationDuration: o.d, animationDelay: o.delay }} />
    ))}

    {/* the living, self-composing network flowing through the panel */}
    <NetworkField count={network} speed={0.15} linkDist={0.34} />

    {/* crisp specular hairline across the top for pristine glass realism */}
    <div className="absolute inset-x-0 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(246,227,166,0.55), transparent)" }} />
  </div>
);

export const HC_STYLES = `
        .hc-crisp{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;text-rendering:optimizeLegibility}
        .hc-title{background:linear-gradient(180deg,#FBF0C9 0%,#E9CE7A 34%,#D4AF37 62%,#A8842A 100%);-webkit-background-clip:text;background-clip:text;color:transparent;text-shadow:0 10px 26px rgba(0,0,0,0.6),0 2px 6px rgba(212,175,55,0.32)}
        .hc-title--bright{background-image:linear-gradient(178deg,#FFFDF7 0%,#FDF3D4 30%,#F4E1A6 58%,#E4C877 100%);-webkit-background-clip:text;background-clip:text;color:transparent;text-shadow:none;filter:drop-shadow(0 2px 10px rgba(0,0,0,0.55)) drop-shadow(0 0 20px rgba(212,175,55,0.22))}
        .hc-ari-text{background:linear-gradient(176deg,#FFFDF6 0%,#FBEFCF 42%,#F1DDA4 74%,#E4C87E 100%);-webkit-background-clip:text;background-clip:text;color:transparent;filter:drop-shadow(0 1px 0 rgba(0,0,0,0.42)) drop-shadow(0 2px 8px rgba(0,0,0,0.35))}
        .hc-figure{background:linear-gradient(178deg,#FFF6DA 0%,#F6DE9C 38%,#E6C46E 66%,#C79A32 100%);-webkit-background-clip:text;background-clip:text;color:transparent;filter:drop-shadow(0 2px 0 rgba(0,0,0,0.42)) drop-shadow(0 10px 26px rgba(212,175,55,0.28)) drop-shadow(0 14px 40px rgba(0,0,0,0.5))}
        @keyframes hcDot{0%,60%,100%{opacity:0.3;transform:translateY(0)}30%{opacity:1;transform:translateY(-2px)}}
        .hc-tdot{animation:hcDot 1.3s infinite ease-in-out}
        @keyframes hcSheen{0%{transform:translateX(-140%) skewX(-18deg)}55%,100%{transform:translateX(260%) skewX(-18deg)}}
        .hc-sheen{position:absolute;top:0;bottom:0;left:0;width:42%;background:linear-gradient(100deg,transparent,rgba(255,255,255,0.16),transparent);mix-blend-mode:screen;pointer-events:none;animation:hcSheen 5s ease-in-out infinite}
        .hc-grain{background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");background-size:170px 170px}
`;

export const HeroCinematic = ({ videoSrc = null }) => {
  const reduce = useReducedMotion();
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const lowPower = reduce || (typeof navigator !== "undefined" && navigator.connection &&
    (navigator.connection.saveData || ["slow-2g", "2g"].includes(navigator.connection.effectiveType)));

  const { lang, dict, register } = useLang();
  const tr = useCallback((t) => {
    if (!t || lang === "en") return t;
    const v = dict[t];
    if (v === undefined) { register(t); return t; }
    return v;
  }, [lang, dict, register]);

  const [phase, setPhase] = useState(lowPower ? "converse" : "presence");
  const [convoIdx, setConvoIdx] = useState(0);
  const [shownIdx, setShownIdx] = useState(0);
  const [contentVis, setContentVis] = useState(1);
  const [stage, setStage] = useState(lowPower ? "answer2" : "idle"); // idle|ask|think|answer|data|ask2|think2|answer2|swap
  const [beat, setBeat] = useState(0); // cinematic story-beat index for the intro
  const convo = CONVOS[shownIdx];

  // Pointer parallax → the whole panel tilts gently and its glow drifts, for a real, dimensional feel.
  const pmx = useMotionValue(0), pmy = useMotionValue(0);
  const tiltRX = useSpring(useTransform(pmy, [-0.5, 0.5], [4, -4]), { stiffness: 50, damping: 22, mass: 0.7, restDelta: 0.01 });
  const tiltRY = useSpring(useTransform(pmx, [-0.5, 0.5], [-5, 5]), { stiffness: 50, damping: 22, mass: 0.7, restDelta: 0.01 });
  const onMove = (e) => { const r = e.currentTarget.getBoundingClientRect(); pmx.set((e.clientX - r.left) / r.width - 0.5); pmy.set((e.clientY - r.top) / r.height - 0.5); };
  const onLeave = () => { pmx.set(0); pmy.set(0); };

  // ---- Pacing: calm, legible beats across the full two-turn exchange. ----
  const G = useMemo(() => (isMobile
    ? { qRead: 1500, think: 1500, aRead: 2100, dataHold: 2100, q2Read: 1500, think2: 1400, a2Read: 2600 }
    : { qRead: 1900, think: 1800, aRead: 2700, dataHold: 2600, q2Read: 1900, think2: 1700, a2Read: 3100 }), [isMobile]);
  const HOLD = isMobile ? 2600 : 3200;
  const converseDur = useCallback(() => Math.round(
    G.qRead + G.think + G.aRead + G.dataHold + G.q2Read + G.think2 + G.a2Read + HOLD + (isMobile ? 800 : 1100)
  ), [G, HOLD, isMobile]);

  // Master timeline.
  useEffect(() => {
    if (lowPower || videoSrc) return;
    let cancelled = false; const timers = [];
    const D = isMobile ? { presence: 13500, brand: 3400 } : { presence: 15500, brand: 4200 };
    const seq = [["presence", 0, D.presence]];
    CONVOS.forEach((_, ci) => seq.push(["converse", ci, converseDur(ci)]));
    seq.push(["brand", 0, D.brand]);
    const run = () => {
      if (cancelled) return; let t = 0;
      seq.forEach(([p, ci, dur]) => {
        timers.push(setTimeout(() => { if (!cancelled) { setPhase(p); setConvoIdx(ci); } }, t));
        t += dur;
      });
      timers.push(setTimeout(() => { if (!cancelled) run(); }, t));
    };
    run();
    return () => { cancelled = true; timers.forEach(clearTimeout); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lowPower, videoSrc, isMobile]);

  // Cinematic story sequencer — advances the intro one dramatic line at a time, then resolves to brand.
  useEffect(() => {
    if (phase !== "presence") { setBeat(0); return; }
    setBeat(0);
    const timers = [];
    for (let i = 1; i <= STORY.length; i++) timers.push(setTimeout(() => setBeat(i), 2000 + i * 3400));
    return () => timers.forEach(clearTimeout);
  }, [phase]);

  // Conversation swap — gentle crossfade to the next restaurant. Keyed remount → clean state.
  useEffect(() => {
    if (lowPower || convoIdx === shownIdx) return;
    if (phase !== "converse") {
      if (phase === "presence" || phase === "brand") setShownIdx(convoIdx);
      return;
    }
    setContentVis(0);
    setStage("swap");
    const t = setTimeout(() => { setShownIdx(convoIdx); setContentVis(1); }, 520);
    return () => clearTimeout(t);
  }, [convoIdx, shownIdx, lowPower, phase]);

  useLayoutEffect(() => {
    if (lowPower) return;
    if (phase === "converse") setStage("ask");
    else if (phase === "presence") setStage("idle");
  }, [phase, shownIdx, lowPower]);

  // Stage machine — the full two-turn beat sequence.
  useEffect(() => {
    if (lowPower || phase !== "converse") return;
    let cancelled = false; const timers = []; let t = 0;
    const step = (dur, st) => { t += dur; timers.push(setTimeout(() => !cancelled && setStage(st), t)); };
    step(G.qRead, "think");
    step(G.think, "answer");
    step(G.aRead, "data");
    step(G.dataHold, "ask2");
    step(G.q2Read, "think2");
    step(G.think2, "answer2");
    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, [phase, shownIdx, G, lowPower]);

  const presenceOn = phase === "presence";
  const converseOn = phase === "converse";
  const brandOn = phase === "brand";
  const has = (arr) => converseOn && arr.includes(stage);
  const managerShown = has(["ask", "think", "answer", "data", "ask2", "think2", "answer2"]);
  const ari1Shown = has(["think", "answer", "data", "ask2", "think2", "answer2"]);
  const ari1Thinking = has(["think"]);
  const answer1Shown = has(["answer", "data", "ask2", "think2", "answer2"]);
  const dataShown = has(["data", "ask2", "think2", "answer2"]);
  const manager2Shown = has(["ask2", "think2", "answer2"]);
  const ari2Shown = has(["think2", "answer2"]);
  const ari2Thinking = has(["think2"]);
  const answer2Shown = has(["answer2"]);
  const thinking = ari1Thinking || ari2Thinking;

  const SETTLE_OUT = 0.55, SETTLE_IN = 1.0, SETTLE_GAP = 0.5;
  const sceneFade = (show) => ({
    initial: false,
    animate: { opacity: show ? 1 : 0 },
    transition: show ? { duration: SETTLE_IN, ease: SLOW, delay: SETTLE_GAP } : { duration: SETTLE_OUT, ease: SLOW },
  });

  if (videoSrc) {
    return (
      <div className="absolute inset-0 bg-[#0A0A0B]" data-testid="hero-cinematic">
        <video className="w-full h-full object-cover hero-video-pristine" src={videoSrc} autoPlay muted loop playsInline preload="metadata" poster="/marketing/screen-dashboard.png" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-black/25 pointer-events-none" />
      </div>
    );
  }

  return (
    <div className="absolute inset-0 flex items-center justify-center" data-testid="hero-cinematic" data-phase={phase} data-stage={stage}
      style={{ perspective: 1900 }} onPointerMove={onMove} onPointerLeave={onLeave}>
      <style>{HC_STYLES}</style>

      {/* ===== PREMIUM GLASS PANEL — the container the whole conversation lives in ===== */}
      <motion.div className="relative w-full h-full overflow-hidden"
        style={{ rotateX: tiltRX, rotateY: tiltRY, border: "1px solid transparent" }}>

        {/* glass surface — tint + backdrop blur on a CLIPPED child, so the backdrop-filter's square
            corners get trimmed to the panel (top rounded, bottom squared). The charcoal-gold now
            fills the ENTIRE interior down to the squared bottom edge — no bottom feather. */}
        <div className="absolute inset-0 z-0 pointer-events-none" style={{
          background: "linear-gradient(160deg, rgba(12,12,15,0.66) 0%, rgba(10,10,12,0.6) 52%, rgba(9,9,12,0.7) 100%)",
          backdropFilter: "blur(22px)", WebkitBackdropFilter: "blur(22px)",
          clipPath: "inset(0)", WebkitClipPath: "inset(0)",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08), inset 0 0 0 1px rgba(255,255,255,0.025)" }} />

        {/* the hero's living environment, now living INSIDE the panel */}
        <PanelAmbience />

        <div className="absolute inset-0 z-10">

          {/* ===== PRESENCE — the ARI introduction: its own slow, cinematic moment ===== */}
          <motion.div data-scene="presence" className="absolute inset-0 flex items-center justify-center pointer-events-none px-7" {...sceneFade(presenceOn)}>
            <div className="relative w-full text-center flex flex-col items-center" style={{ maxWidth: 440 }}>

              {/* ARI orb — dramatic, razor-sharp, the constant hero of the scene (clean tight glow, minimal blur) */}
              <div className="relative flex items-center justify-center" style={{ height: isMobile ? 156 : 216 }}>
                <motion.span aria-hidden className="absolute rounded-full"
                  style={{ width: 300, height: 300, background: "radial-gradient(circle, rgba(212,175,55,0.24) 0%, rgba(212,175,55,0.07) 40%, transparent 66%)" }}
                  initial={false} animate={{ opacity: presenceOn ? 1 : 0, scale: presenceOn ? [0.95, 1.06, 0.95] : 0.55 }}
                  transition={{ opacity: { duration: 2.2, ease: SLOW }, scale: { duration: 10, ease: "easeInOut", repeat: Infinity } }} />

                {/* POWER-ON FLARE — a slow bright bloom that ignites as the orb comes online */}
                <motion.span aria-hidden className="absolute rounded-full"
                  style={{ width: 150, height: 150, background: "radial-gradient(circle, rgba(255,251,230,0.98) 0%, rgba(251,236,161,0.5) 36%, transparent 72%)", mixBlendMode: "screen" }}
                  initial={false} animate={{ opacity: presenceOn ? [0, 1, 0] : 0, scale: presenceOn ? [0.12, 1.5, 3] : 0.12 }}
                  transition={{ duration: 2.6, ease: SLOW, delay: presenceOn ? 0.15 : 0, times: [0, 0.4, 1] }} />
                <motion.span aria-hidden className="absolute rounded-full"
                  style={{ width: 186, height: 186, border: "1px solid rgba(251,236,161,0.9)", boxShadow: "0 0 26px rgba(212,175,55,0.5)" }}
                  initial={false} animate={{ opacity: presenceOn ? [0, 0.9, 0] : 0, scale: presenceOn ? [0.26, 1.25, 2.2] : 0.26 }}
                  transition={{ duration: 3.0, ease: SLOW, delay: presenceOn ? 0.3 : 0, times: [0, 0.36, 1] }} />

                {/* the orb — igniting from a spark */}
                <motion.div initial={false}
                  animate={{ opacity: presenceOn ? 1 : 0, scale: presenceOn ? 1 : 0.4, rotateZ: presenceOn ? 0 : -12, y: presenceOn ? 0 : 12 }}
                  transition={{ duration: 2.4, ease: SLOW, delay: presenceOn ? 0.25 : 0 }}>
                  <AriOrb size={isMobile ? 138 : 196} state="insight" />
                </motion.div>
              </div>

              {/* CINEMATIC STORY — one dramatic line at a time, resolving to the ARI brand frame */}
              <div className="relative mt-9 w-full flex items-start justify-center" style={{ minHeight: isMobile ? 132 : 158 }}>
                <AnimatePresence mode="wait">
                  {beat < STORY.length ? (
                    <motion.p key={`beat-${beat}`} data-no-translate
                      className="hc-crisp font-serif-lux text-white/92 text-[1.5rem] sm:text-[1.95rem] leading-[1.22] tracking-tight px-3"
                      initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
                      transition={{ duration: 1.7, ease: SLOW }}>
                      {tr(STORY[beat])}
                    </motion.p>
                  ) : (
                    <motion.div key="brand" className="flex flex-col items-center"
                      initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.4, ease: SLOW }}>
                      <motion.div data-no-translate className="hc-crisp font-mono-lux text-[10px] uppercase mb-3" style={{ color: "#EBD182" }}
                        initial={{ letterSpacing: "0.95em", opacity: 0 }} animate={{ letterSpacing: "0.5em", opacity: 1 }} transition={{ duration: 1.9, ease: SLOW }}>
                        {tr("Meet ARI")}
                      </motion.div>
                      <div className="hc-title hc-title--bright font-serif-lux font-bold leading-[1.08] tracking-tight text-[1.85rem] sm:text-[2.2rem]">
                        {[tr("An intelligence for"), tr("your restaurant")].map((line, li) => (
                          <motion.span key={li} data-no-translate className="block"
                            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.5, ease: SLOW, delay: 0.4 + li * 0.35 }}>{line}</motion.span>
                        ))}
                      </div>
                      <motion.div className="mt-5 inline-flex items-center gap-2"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.4, ease: SLOW, delay: 1.4 }}>
                        <span aria-hidden className="text-base" style={{ color: GOLD, filter: "drop-shadow(0 0 8px rgba(212,175,55,0.6))" }}>→</span>
                        <span data-no-translate className="hc-crisp text-[13px] sm:text-[14px] text-white/82 leading-snug">
                          {tr("prompting your team before a moment slips.")}
                        </span>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>

          {/* ===== CONVERSATION — a rich two-turn ARI chat inside the panel ===== */}
          <motion.div data-scene="converse" className="absolute inset-0 flex flex-col" {...sceneFade(converseOn)}>
            <motion.div key={shownIdx} className="flex flex-col h-full"
              initial={{ opacity: 0 }} animate={{ opacity: contentVis }} transition={{ duration: contentVis ? 0.7 : 0.4, ease: SLOW }}>

              {/* panel header — the owner/manager whose workspace this is */}
              <div className="flex items-center gap-2.5 px-4 py-3 border-b border-white/[0.07]"
                style={{ background: "linear-gradient(180deg,rgba(255,255,255,0.04),transparent)" }}>
                <span data-no-translate className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center hc-crisp text-[11px] font-semibold text-white tracking-tight"
                  style={{ background: "linear-gradient(155deg,#C3ACF6,#7E68BE)", border: "1px solid rgba(183,156,242,0.5)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.38),0 6px 14px -6px rgba(0,0,0,0.6)" }}>
                  {convo.owner.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                </span>
                <div className="min-w-0 flex-1 leading-tight">
                  <p data-no-translate className="hc-crisp text-[13px] font-semibold text-white tracking-tight">{convo.owner}</p>
                  <p className="hc-crisp text-[10px] text-white/45 truncate flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#5FD3B0", boxShadow: "0 0 6px rgba(95,211,176,0.7)" }} />
                    <span data-no-translate>{tr(convo.role)} · {convo.restaurant}</span>
                  </p>
                </div>
                <span data-no-translate className="hc-crisp font-mono-lux text-[9px] tracking-[0.24em] uppercase text-white/30">{tr("Live")}</span>
              </div>

              {/* thread — newest at the bottom; older turns slide up as the exchange grows */}
              <div className="flex-1 flex flex-col justify-end gap-2.5 px-4 pt-3 pb-2 overflow-hidden">
                {managerShown && <ManagerBubble testId="hero-manager-bubble" text={tr(convo.q)} time="09:32" />}
                {ari1Shown && <AriTurn thinking={ari1Thinking} answerShown={answer1Shown} text={tr(convo.a)} time="09:32" />}

                {dataShown && (
                <motion.div layout data-testid="hero-data-figure" className="self-stretch shrink-0 relative overflow-hidden w-full rounded-2xl p-3.5"
                  style={{ background: "linear-gradient(180deg,rgba(212,175,55,0.12),rgba(212,175,55,0.03))", border: `1px solid ${ARI.border}`, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.14),0 16px 36px -18px rgba(0,0,0,0.8)", transformPerspective: 780 }}
                  initial={{ opacity: 0, y: 16, rotateX: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, rotateX: 0, scale: 1 }} transition={{ type: "spring", stiffness: 120, damping: 18, mass: 0.85 }}>
                  <span aria-hidden className="hc-sheen" style={{ animationDuration: "6s" }} />
                  <div className="flex items-center justify-between">
                    <span data-no-translate className="hc-crisp font-mono-lux text-[8.5px] tracking-[0.18em] uppercase text-white/55">{tr(convo.vis.title)}</span>
                    <span className="hc-crisp text-[8.5px] font-mono-lux uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full"
                      style={{ color: ARI.solid, border: `1px solid ${ARI.solid}44`, background: `${ARI.solid}16` }}>{tr(convo.vis.delta)}</span>
                  </div>
                  <div className="flex items-end justify-between mt-2 gap-3">
                    <span data-no-translate className="hc-crisp hc-figure font-serif-lux font-semibold text-[2.5rem] leading-[1.28] tracking-tight pb-1">
                      <CountUp to={convo.vis.value} active={dataShown} />{convo.vis.unit}
                    </span>
                    <div className="flex items-end gap-[3px] h-9 pb-1">
                      {convo.vis.spark.map((h, i) => (
                        <motion.span key={i} className="w-[5px] rounded-full"
                          style={{ background: i === convo.vis.spark.length - 1 ? ARI.solid : "rgba(212,175,55,0.32)", boxShadow: i === convo.vis.spark.length - 1 ? `0 0 8px ${ARI.glow}` : "none" }}
                          initial={{ height: 2 }} animate={{ height: `${h}%` }} transition={{ duration: 0.7, ease: EASE, delay: 0.2 + i * 0.05 }} />
                      ))}
                    </div>
                  </div>
                </motion.div>
                )}

                {manager2Shown && <ManagerBubble testId="hero-manager-bubble-2" text={tr(convo.q2)} time="09:33" />}
                {ari2Shown && <AriTurn thinking={ari2Thinking} answerShown={answer2Shown} text={tr(convo.a2)} time="09:33" />}
              </div>

              {/* input bar */}
              <div className="px-4 pb-3.5 pt-1">
                <motion.div className="flex items-center gap-2 rounded-full pl-2.5 pr-1.5 py-1.5" initial={false}
                  style={{ border: "1px solid rgba(255,255,255,0.10)", background: "linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.015))", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.1)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}
                  animate={{ opacity: converseOn ? 1 : 0, y: converseOn ? 0 : 8 }} transition={{ duration: 0.6, ease: SLOW, delay: 0.2 }}>
                  <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 bg-white/8"><Plus size={13} className="text-white/45" strokeWidth={2.4} /></span>
                  <span data-no-translate className="hc-crisp text-[12px] text-white/35 flex-1 truncate">{tr("Ask ARI anything…")}</span>
                  <span className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: ARI.grad, boxShadow: "inset 0 1px 1px rgba(255,255,255,0.7),inset 0 -2px 3px rgba(0,0,0,0.25),0 4px 10px -3px rgba(0,0,0,0.5)" }}>
                    <ArrowUp size={14} strokeWidth={2.8} className="text-[#1a1305]" />
                  </span>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>

          {/* ===== BRAND ===== */}
          <motion.div data-scene="brand" className="absolute inset-0 flex items-center justify-center pointer-events-none px-6" {...sceneFade(brandOn)}>
            <motion.div data-no-translate className="text-center"
              initial={false} animate={{ scale: brandOn ? 1 : 0.86, y: brandOn ? 0 : 18 }} transition={{ duration: 1.3, ease: SLOW }}>
              <div className="flex items-center justify-center gap-2 mb-3">
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#5FD3B0", boxShadow: "0 0 8px #5FD3B0" }} />
                <span className="font-mono-lux text-[9px] sm:text-[11px] tracking-[0.34em] uppercase text-white/50">{tr("Intelligence online")}</span>
              </div>
              <div className="hc-title font-serif-lux font-bold leading-[1.02] tracking-tight text-2xl sm:text-4xl">
                Admin Restaurant<br className="hidden sm:block" /> Intelligence
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* soft glass sheen only — no grain, for a pristine polished surface */}
        <div className="absolute inset-0 z-[46] pointer-events-none" style={{ background: "linear-gradient(180deg,rgba(255,255,255,0.04) 0%,transparent 16%)" }} />
      </motion.div>
    </div>
  );
};
